const MONTHS = {
  ene: 0, enero: 0,
  feb: 1, febrero: 1,
  mar: 2, marzo: 2,
  abr: 3, abril: 3,
  may: 4, mayo: 4,
  jun: 5, junio: 5,
  jul: 6, julio: 6,
  ago: 7, agosto: 7,
  sep: 8, sept: 8, septiembre: 8,
  set: 8,
  oct: 9, octubre: 9,
  nov: 10, noviembre: 10,
  dic: 11, diciembre: 11,
};

function resolveMonth(token) {
  const t = String(token || '').trim().toLowerCase();
  if (!t) return null;
  if (MONTHS[t] != null) return MONTHS[t];
  const k4 = t.slice(0, 4);
  if (MONTHS[k4] != null) return MONTHS[k4];
  const k3 = t.slice(0, 3);
  if (MONTHS[k3] != null) return MONTHS[k3];
  return null;
}

export function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function inferEventYear(month, day, explicitYear) {
  if (explicitYear != null && !Number.isNaN(explicitYear)) return explicitYear;
  const today = startOfToday();
  let year = today.getFullYear();
  const candidate = new Date(year, month, day);
  candidate.setHours(0, 0, 0, 0);
  if (candidate.getTime() < today.getTime()) year += 1;
  return year;
}

function buildLocalDate(year, month, day) {
  const d = new Date(year, month, day);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

function normalizeSpanishDateString(raw) {
  let t = String(raw || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (!t || /^por concretar$/i.test(t)) return '';

  t = t.replace(/\b(lunes|martes|miercoles|jueves|viernes|sabado|domingo)\b/g, ' ');
  t = t.replace(/[.,]/g, ' ');
  t = t.replace(/\s+de\s+/g, ' ');
  t = t.replace(/\s+/g, ' ').trim();

  const range = t.match(/^(\d{1,2})\s*(?:-|al?)\s*\d{1,2}\s+([a-z]+)(?:\s+(\d{4}))?(?:\s|$)/);
  if (range) {
    t = `${range[1]} ${range[2]}${range[3] ? ` ${range[3]}` : ''}`.trim();
  }

  return t;
}

/**
 * Parsea fechas de evento en español (n8n, usuarios, fuente Tavily).
 * Devuelve Date local a medianoche o null.
 */
export function parseComunidadEventDate(raw) {
  const s = normalizeSpanishDateString(raw);
  if (!s) return null;

  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const parts = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (parts) {
      return buildLocalDate(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
    }
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      d.setHours(0, 0, 0, 0);
      return d;
    }
  }

  const slash = s.match(/^(\d{1,2})[/-](\d{1,2})(?:[/-](\d{2,4}))?/);
  if (slash) {
    const dd = Number(slash[1]);
    const mm = Number(slash[2]) - 1;
    let yy = slash[3] ? Number(slash[3]) : null;
    if (yy != null && yy < 100) yy += 2000;
    const year = inferEventYear(mm, dd, yy);
    return buildLocalDate(year, mm, dd);
  }

  const dayMonth = s.match(/^(\d{1,2})\s+([a-z]+)(?:\s+(\d{4}))?/);
  if (dayMonth) {
    const dd = Number(dayMonth[1]);
    const mon = resolveMonth(dayMonth[2]);
    if (mon == null) return null;
    const year = inferEventYear(mon, dd, dayMonth[3] ? Number(dayMonth[3]) : null);
    return buildLocalDate(year, mon, dd);
  }

  const monthDay = s.match(/^([a-z]+)\s+(\d{1,2})(?:\s+(\d{4}))?/);
  if (monthDay) {
    const mon = resolveMonth(monthDay[1]);
    if (mon == null) return null;
    const dd = Number(monthDay[2]);
    const year = inferEventYear(mon, dd, monthDay[3] ? Number(monthDay[3]) : null);
    return buildLocalDate(year, mon, dd);
  }

  const monthYear = s.match(/^([a-z]+)\s+(\d{4})$/);
  if (monthYear) {
    const mon = resolveMonth(monthYear[1]);
    if (mon == null) return null;
    return buildLocalDate(Number(monthYear[2]), mon, 1);
  }

  return null;
}

export function comunidadEventDateRaw(ev) {
  const cuerpoFecha = ev?._cuerpo && typeof ev._cuerpo === 'object' ? ev._cuerpo.fecha : null;
  if (cuerpoFecha && String(cuerpoFecha).trim() && !/^por concretar$/i.test(String(cuerpoFecha).trim())) {
    return String(cuerpoFecha).trim();
  }
  if (ev?.fuenteFecha && String(ev.fuenteFecha).trim()) {
    return String(ev.fuenteFecha).trim();
  }
  const display = ev?.date;
  if (display && String(display).trim() && !/^por concretar$/i.test(String(display).trim())) {
    return String(display).trim();
  }
  return '';
}

export function getComunidadEventDate(ev) {
  return parseComunidadEventDate(comunidadEventDateRaw(ev));
}

/** Timestamp para ordenar feed (Infinity si no hay fecha). */
export function comunidadEventDateSortKey(ev) {
  const d = getComunidadEventDate(ev);
  return d ? d.getTime() : Infinity;
}

/** Ordena filas admin/feed por fecha de evento; sin fecha al final. */
export function sortComunidadRowsByEventDate(rows, direction = 'asc') {
  const mult = direction === 'desc' ? -1 : 1;
  return [...(rows || [])].sort((a, b) => {
    const ka = comunidadEventDateSortKey(rowToEventDateInput(a));
    const kb = comunidadEventDateSortKey(rowToEventDateInput(b));
    const aMissing = ka === Infinity;
    const bMissing = kb === Infinity;
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;
    return (ka - kb) * mult;
  });
}

function rowToEventDateInput(row) {
  return {
    _cuerpo: row?.cuerpo && typeof row.cuerpo === 'object' ? row.cuerpo : {},
    fuenteFecha: row?.fuente_fecha,
    date: row?.cuerpo?.fecha,
  };
}
